/**
 * Bounds for the column resize handle, derived from the template JSON.
 *
 * Nothing here touches the DOM: the canvas carries a zoom-fit scale, and
 * mixing `getBoundingClientRect` (scaled) with `clientWidth` (not scaled)
 * silently divides the result by that scale.
 */

export const MIN_COLUMN_PERCENT = 10;

/** Minimal shape this module needs from an MJML node. */
export type SpacedNode = {
  attributes?: Record<string, unknown>;
  children?: SpacedNode[];
};

export type ColumnResizeBounds = {
  min: number;
  max: number;
};

/**
 * A cell cannot lay out below one character of its own text. `word-break:
 * break-word` lets text wrap that far but no further, so buttons and headings
 * still need room for their widest glyph.
 */
const ONE_CHARACTER_PX = 16;

/**
 * Horizontal padding of a node in px, from the `<prefix>padding` shorthand with
 * any `<prefix>padding-left` / `-right` longhand applied on top, as CSS
 * resolves it. Blocks that render several parts namespace their spacing -
 * `title-padding`, `button-inner-padding` - hence the prefix.
 */
export function getHorizontalPadding(
  attributes: Record<string, unknown> = {},
  prefix = ""
): number {
  const parts = String(attributes[`${prefix}padding`] ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  let left = 0;
  let right = 0;
  if (parts.length === 1) {
    left = right = parseFloat(parts[0]) || 0;
  } else if (parts.length > 1) {
    right = parseFloat(parts[1]) || 0;
    left = parts.length > 3 ? parseFloat(parts[3]) || 0 : right;
  }

  if (attributes[`${prefix}padding-left`] != null) {
    left = parseFloat(String(attributes[`${prefix}padding-left`])) || 0;
  }
  if (attributes[`${prefix}padding-right`] != null) {
    right = parseFloat(String(attributes[`${prefix}padding-right`])) || 0;
  }

  return left + right;
}

/**
 * Every padding namespace a node declares: `""` for its own `padding`,
 * `"button-"` for `button-padding`, and so on.
 */
function getPaddingPrefixes(attributes: Record<string, unknown>): string[] {
  const prefixes = new Set<string>();
  for (const key of Object.keys(attributes)) {
    const match = /^(.*?)padding(-left|-right|-top|-bottom)?$/.exec(key);
    if (match) {
      prefixes.add(match[1]);
    }
  }
  return [...prefixes];
}

/**
 * Width in px the node's own chrome takes, whatever it wraps.
 *
 * `inner-padding` sits inside `padding` so the two add up; separate namespaces
 * (`title-`, `price-`, `button-`) are parts stacked above each other, so the
 * widest one wins rather than their sum.
 */
function getOwnMinWidthPx(attributes: Record<string, unknown> = {}): number {
  // A block with `inner-padding` wraps its label in a nested table - a button -
  // which still needs room for one glyph next to that padding.
  const own =
    getHorizontalPadding(attributes) +
    getHorizontalPadding(attributes, "inner-") +
    (attributes["inner-padding"] != null ? ONE_CHARACTER_PX : 0);

  let widestPart = 0;
  for (const prefix of getPaddingPrefixes(attributes)) {
    if (prefix === "" || prefix.endsWith("inner-")) {
      continue;
    }
    widestPart = Math.max(
      widestPart,
      getHorizontalPadding(attributes, prefix) +
        getHorizontalPadding(attributes, `${prefix}inner-`) +
        ONE_CHARACTER_PX
    );
  }

  return own + widestPart;
}

/**
 * Narrowest width in px the node can render at.
 *
 * Mostly padding. Text wraps down to a single character (`word-break:
 * break-word` on every block cell), images are fluid (`max-width: 100%`) and
 * rating segments wrap, so what a column cannot give back is the fixed px
 * padding of its cells - `inner-padding` and namespaced part padding included.
 * Children are taken by max rather than summed, which keeps the estimate
 * permissive for loop and group blocks.
 */
export function getMinWidthPx(node: SpacedNode | undefined): number {
  if (!node) {
    return 0;
  }

  const childMin = (node.children ?? []).reduce(
    (widest, child) => Math.max(widest, getMinWidthPx(child)),
    0
  );

  return getOwnMinWidthPx(node.attributes) + childMin;
}

/** Width the columns of a section share out: body width minus section padding. */
export function getSectionContentWidth(
  section: SpacedNode | undefined,
  bodyWidth: number
): number {
  return Math.max(0, bodyWidth - getHorizontalPadding(section?.attributes));
}

function toPercent(px: number, contentWidth: number): number {
  return contentWidth > 0 ? (px / contentWidth) * 100 : 0;
}

/**
 * Share of the section already spoken for by the columns the handle does not
 * move. A column with no width parses as 0, which is what the renderer does
 * with it too.
 */
export function getOtherColumnsPercent(
  columns: SpacedNode[],
  columnIndex: number
): number {
  return columns.reduce((total, column, index) => {
    if (index === columnIndex || index === columnIndex + 1) {
      return total;
    }
    return total + (parseFloat(String(column.attributes?.width ?? "")) || 0);
  }, 0);
}

/** Percent the resized pair has to split between them. */
export function getAvailablePercent(
  columns: SpacedNode[],
  columnIndex: number
): number {
  return 100 - getOtherColumnsPercent(columns, columnIndex);
}

/**
 * Whether the pair can be resized at all. A template whose other columns
 * already claim more than 80% leaves no split where both keep their floor, and
 * writing one anyway would produce a negative width.
 */
export function canResizePair(
  columns: SpacedNode[],
  columnIndex: number
): boolean {
  return getAvailablePercent(columns, columnIndex) >= 2 * MIN_COLUMN_PERCENT;
}

/**
 * Range the dragged column may move within. The handle only moves the boundary
 * between `columnIndex` and the column to its right, so the other columns keep
 * their share for the whole drag.
 *
 * When the two columns cannot both keep their content floor those are dropped
 * and only `MIN_COLUMN_PERCENT` applies - an unsatisfiable template must still
 * leave the handle usable, otherwise it dies with no explanation. Callers must
 * check `canResizePair` first; the bounds assume the pair has room for two
 * floors.
 */
export function getColumnResizeBounds(params: {
  columns: SpacedNode[];
  columnIndex: number;
  contentWidth: number;
}): ColumnResizeBounds {
  const { columns, columnIndex, contentWidth } = params;

  const available = getAvailablePercent(columns, columnIndex);
  const absoluteMin = MIN_COLUMN_PERCENT;
  const absoluteMax = available - MIN_COLUMN_PERCENT;

  // Round the minimum up and the maximum down so the rendered width covers the
  // floor rather than landing a fraction of a pixel short.
  const min = Math.max(
    absoluteMin,
    Math.ceil(toPercent(getMinWidthPx(columns[columnIndex]), contentWidth))
  );
  const max = Math.min(
    absoluteMax,
    Math.floor(
      available -
        toPercent(getMinWidthPx(columns[columnIndex + 1]), contentWidth)
    )
  );

  if (min > max) {
    return { min: absoluteMin, max: Math.max(absoluteMin, absoluteMax) };
  }

  return { min, max };
}

export function clampColumnPercent(
  requested: number,
  bounds: ColumnResizeBounds
): number {
  return Math.min(Math.max(requested, bounds.min), bounds.max);
}
